class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState) {
    // The custom header's cart link is inside japanime-header, not a Dawn header
    // section. Refresh the entire drawer from its own section and derive its
    // count from that same response, so the two surfaces cannot disagree.
    const parseDrawer = (html) => {
      if (typeof html !== 'string' || !html.trim()) return null;
      return new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
    };
    const hasVisibleItems = (drawer) => {
      const count = Number(drawer?.querySelector('.drawer__heading span')?.textContent?.trim());
      return !!drawer && !drawer.classList.contains('is-empty') && count > 0 && !!drawer.querySelector('#CartDrawer-CartItems .cart-item');
    };

    let source = parseDrawer(parsedState.sections?.['cart-drawer']);
    if (!hasVisibleItems(source)) {
      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`, { cache: 'no-store' });
      if (!response.ok) throw new Error('Cart drawer section could not be refreshed');
      source = parseDrawer(await response.text());
    }
    if (!hasVisibleItems(source)) throw new Error('Cart drawer returned no visible items after adding a product');

    const count = source.querySelector('.drawer__heading span')?.textContent?.trim();

    // Commit only after the full, nonempty section has been checked. Leaving
    // the previous markup intact makes a failed refresh recoverable.
    this.innerHTML = source.innerHTML;
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));

    const cartLink = document.getElementById('cart-icon-bubble');
    if (cartLink) {
      const countElement = cartLink.querySelector('.jp-header-action__count');
      if (countElement) countElement.textContent = count;
      const accessibleCount = cartLink.querySelector('.visually-hidden');
      if (accessibleCount) accessibleCount.textContent = accessibleCount.textContent.replace(/\d+/, count);
    }
    this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
